import type { ComponentType } from "react";
import {
  Bell,
  Key,
  Laptop,
  Palette,
  Password,
  ShieldCheck,
  User,
  Users,
  Wrench,
  type IconProps
} from "@phosphor-icons/react";
import { keyForSegment, SETTINGS_SEGMENT } from "../../lib/englishPaths";

/**
 * Inställningar as two groups, the second build round of 2026-09-24 (`prototype/kund/pages.js`,
 * `SETTINGS`).
 *
 * Eight panels in one column became two lists split the way design.md section 3 splits "du" and
 * "ni": what belongs to the person's own account, and what belongs to the company. Each section is an
 * address of its own under `/settings/:section`, so a notification or Översikt can open exactly one,
 * and the names are Swedish because a client reads them in the address bar.
 *
 * Hemligheter is listed in the company's group and is not a section: it is its own page at
 * `/secrets`, and the row goes there. The same holds for the last row of the person's own group,
 * added 2026-10-06: the privacy information is the public page `/privacy`, and it is listed under
 * "Ditt konto" because it is about what the portal keeps on the person reading it.
 */
export type SettingsSectionKey =
  | "konto"
  | "sakerhet"
  | "inloggningar"
  | "notiser"
  | "utseende"
  | "forvaltning"
  | "personer";

export interface SettingsEntry {
  key: SettingsSectionKey | "hemligheter" | "integritet";
  label: string;
  icon: ComponentType<IconProps>;
  to: string;
}

export const ACCOUNT_ENTRIES: SettingsEntry[] = [
  { key: "konto", label: "Namn och inloggning", icon: User, to: "/settings/account" },
  { key: "sakerhet", label: "Lösenord och tvåfaktor", icon: Password, to: "/settings/security" },
  { key: "inloggningar", label: "Inloggningar", icon: Laptop, to: "/settings/sessions" },
  { key: "notiser", label: "Notiser och den här enheten", icon: Bell, to: "/settings/notifications" },
  { key: "utseende", label: "Utseende", icon: Palette, to: "/settings/appearance" },
  { key: "integritet", label: "Så hanterar jag dina uppgifter", icon: ShieldCheck, to: "/privacy" }
];

export const COMPANY_ENTRIES: SettingsEntry[] = [
  { key: "forvaltning", label: "Förvaltning", icon: Wrench, to: "/settings/management" },
  { key: "personer", label: "Personer med åtkomst", icon: Users, to: "/settings/people" },
  { key: "hemligheter", label: "Hemligheter", icon: Key, to: "/secrets" }
];

/** The seven sections that are addresses under `/settings`, in the order the list shows them. */
export const SECTIONS = [...ACCOUNT_ENTRIES, ...COMPANY_ENTRIES].filter(
  (entry): entry is SettingsEntry & { key: SettingsSectionKey } =>
    entry.key !== "hemligheter" && entry.key !== "integritet"
);

/** The section an address segment names. The segment is English since build round 34 (2026-10-10). */
export function sectionFor(segment: string | undefined) {
  const key = keyForSegment(SETTINGS_SEGMENT, segment);
  return key ? SECTIONS.find((entry) => entry.key === key) ?? null : null;
}

/**
 * The anchors the one long page had, and the section each now lives in.
 *
 * Översikt linked to `/settings#sessions-title` and `/settings#management-title` until the page was
 * split, and a notification or a bookmark may still carry one of them. Such a link is sent to the
 * section rather than landing on the list with the anchor pointing at nothing. The ids stay on the
 * panels' headings, so a link that names a section and an anchor still scrolls.
 */
export const LEGACY_ANCHORS: Record<string, SettingsSectionKey> = {
  "profile-title": "konto",
  "password-title": "sakerhet",
  "factor-title": "sakerhet",
  "sessions-title": "inloggningar",
  "device-title": "notiser",
  "preferences-title": "notiser",
  "access-title": "personer",
  "management-title": "forvaltning"
};
